import { createLanguageTestApi } from "@/api/services/global/master";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Textarea } from "@/components/elements/Textarea";
import { Modal } from "@/components/elements/Modal";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { CreateLanguageTestFormTable } from "@/features/master-data/components/LanguageTest/CreateLanguageTestFormTable";
import { languageTestSchema } from "@/features/master-data/schema/masterDataLanguageTestSchema";
import { TLanguageTestForm } from "@/features/master-data/types/masterDataLanguageTestForm";
import { setErrorForm, showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormLabel, Grid, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";

type TCreateLanguageTestFormModalProps = {
  onClose: () => void;
  isOpen: boolean;
};

const defaultFormValues: TLanguageTestForm = {
  testName: "",
  description: "",
  levels: [
    {
      level: "",
    },
  ],
};

export const CreateLanguageTestFormModal: FC<TCreateLanguageTestFormModalProps> = ({ onClose, isOpen }) => {
  const queryClient = useQueryClient();

  const methods = useForm<TLanguageTestForm>({
    resolver: yupResolver(languageTestSchema),
    mode: "onBlur",
    defaultValues: defaultFormValues,
  });

  const { control, reset, watch } = methods;

  const { append, remove } = useFieldArray({
    control,
    name: "levels",
  });

  useEffect(() => {
    if (isOpen) {
      reset(defaultFormValues);
      remove();
      append({ level: "" });
    }
  }, [isOpen, reset, remove, append]);

  const onSubmit = async (data: TLanguageTestForm) => {
    try {
      const rs = await createLanguageTestApi({
        testName: data.testName,
        description: data.description,
        levels: data.levels.map((item) => ({
          level: item.level,
        })),
      });

      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GLOBAL.MASTER_LANGUAGE_TEST],
      });

      reset(defaultFormValues);
      remove();
      append({ level: "" });

      onClose();
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Modal width={1200} title="追加" isOpen={isOpen} enableFooter={false} onClose={onClose} enableCloseIcon={false}>
      <Form methods={methods} onSubmit={onSubmit} schema={languageTestSchema}>
        <Grid container spacing={3} py={3}>
          <Grid item xs={6}>
            <FormField control={control} name="testName">
              <Input />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="description">
              <Textarea />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <Stack gap={2}>
              <FormField>
                <FormLabel required>レベル</FormLabel>
              </FormField>
              <CreateLanguageTestFormTable />
            </Stack>
          </Grid>
        </Grid>
        <Stack flexDirection="row" justifyContent="center" gap="24px">
          <Button variant="outline" onClick={onClose}>
            キャンセル
          </Button>
          <Button type="submit" disabled={watch("levels").length === 0}>
            保存
          </Button>
        </Stack>
      </Form>
    </Modal>
  );
};
